import React, { useRef, useState } from 'react';
import { SignInButton, SignUpButton } from '@clerk/react';
import { T } from '../designTokens';
import { clerkAppearance } from '../clerkAppearance';
import { welcomeRedirectUrl } from '../utils/signupConversion';
import { buildPaymentLinkUrl, stashPostPurchaseRoute } from '../utils/paymentLink';
import { formatPriceFromCents, formatPackContents } from '../utils/price';
import { CONSUMER_UNLOCK_DESC } from '../utils/consumerCopy';
import { FULL_AUDIT_CHECK_COUNT, trackInitiateCheckout, readMetaAttribution } from '@privisy/ui-bridge';
import { MONITOR_ANNUAL_PRICE_CENTS } from '@privisy/domain';
import { unlockReport, createBillingCheckoutSession, createUnlockCheckout, recordPaywallClick } from '../utils/api';
import { ApiError, isInsufficientCredits } from '../utils/api-error';
import { billingRedirect } from '../utils/billing';
import { freeAuditUrl, sampleReportUrl } from '../utils/freeAudit';

export type PaywallState = 'signed-out' | 'unlimited' | 'has-credits' | 'out-of-credits' | 'none';

export interface PaywallOfferProps {
  state: PaywallState;
  userId?: string | null;
  /** Domain or URL the offer is about. Omitted on the scan-submit path. */
  target?: string | null;
  balance?: number | null;
  /**
   * Pack pricing from useCreditInfo. Optional because it is genuinely absent
   * until GET /api/credits answers (#970) — never because a default belongs
   * here. When either value is missing the buy control is omitted rather
   * than rendered from a guess: both are env-configurable
   * (CREDIT_PACK_PRICE_CENTS, CREDITS_PER_PURCHASE), and a stale advertised
   * price is a charging-adjacent defect, not a cosmetic one.
   */
  packPrice?: number;
  creditsPerPurchase?: number;
  onRunFullAudit?: () => void;
  signInButton?: React.ReactNode;
  /**
   * Which surface this offer renders on. 'report' (default) is the
   * original locked-report copy from Task 2 (LockedReportView), unchanged
   * and byte-identical to before this prop existed. 'scan' is the
   * scan-submit and post-claim auto-start surfaces (issue #943 fix round
   * 1): there is no report to "come back to" on those paths, so the
   * out-of-credits copy instead states plainly that the scan did not run
   * and why, while carrying the same offer. 'scan-entry' is the scanner
   * homepage before any submit (issue #971): nothing has been attempted
   * yet, so it describes the account rather than a failed action, and it
   * stands in for the scan form a zero balance cannot use.
   * 'report-delivered' is issue #953: the moment *after* a full report
   * lands. Nothing has been refused and nothing is locked, so the copy
   * introduces the credit model as something the account has rather than
   * something it has spent, and the surface stays silent for any account it
   * cannot describe.
   * 'unlock' is #1762: a locked full audit that already ran every check.
   * The offer is to unlock that report in place (a credit, a purchase, or
   * monitoring), never to rerun it.
   */
  context?: 'report' | 'scan' | 'scan-entry' | 'report-delivered' | 'unlock';
  /** The locked report to unlock. Required by the 'unlock' context. */
  reportId?: string | null;
  /** Called once the 'unlock' context has unlocked the report, to refresh it. */
  onUnlocked?: () => void;
  /**
   * #1779: the signed-out visitor holding this report's claim identity. With
   * it, the signed-out 'unlock' context offers "Unlock this report": the
   * click records the unlock intent, then sign-up (or sign-in) returns to
   * `returnHash`, where the claim and then the unlock checkout run. Without
   * it the signed-out offer is unchanged.
   */
  signedOutUnlock?: SignedOutUnlock;
  /**
   * #2046: a /check visitor's locked report. The 'unlock' offers describe
   * what the unlock shows a visitor instead of selling fixes; the price and
   * buttons are unchanged.
   */
  consumer?: boolean;
  /**
   * The report has no failed check and nothing to review, so the 'unlock'
   * offers describe the record it holds instead of promising fixes.
   */
  clean?: boolean;
}

export interface SignedOutUnlock {
  /** The report's own route, e.g. `#/scan/<id>`, where sign-up lands. */
  returnHash: string;
  /** Records the unlock intent. Runs before the Clerk modal opens. */
  onUnlockSignedOut: () => void;
  /**
   * Persists the claim identity to cookies before redirecting to Stripe, so a
   * cancel return (`#/scan/<id>`, no query params) keeps the claim token.
   */
  onBeforeCheckout?: () => void;
  /**
   * The report's claim credential (2026-10-27). With it the visitor pays
   * first and the webhook makes the account from the checkout email; without
   * it the offer falls back to sign-up first.
   */
  intentToken?: string | null;
  /**
   * #2130: the pay-first card's "keep this report" sign-up. Persists the claim
   * identity before the Clerk modal opens and records no unlock intent, so
   * sign-up claims the report without opening checkout.
   */
  onKeepSignedOut?: () => void;
}

/**
 * Maps raw auth + balance into exactly one state. Exported for reuse and
 * testing. Reproduces LockedReportView's existing branch order exactly:
 * signed-out wins first, then balance === null (and only null) -> unlimited,
 * then balance >= 1 -> has-credits, then balance === 0 -> out-of-credits.
 * Anything left unmatched — balance === undefined (not yet resolved),
 * negative, or otherwise unrecognized — falls through to 'none', which
 * renders nothing: an unresolved balance must never be presented as
 * unlimited access.
 *
 * `creditsResolved` is required, not optional (#970). `balance === null`
 * legitimately means unlimited for PRIVISY_PRO_USER_IDS members and admins,
 * and it is also what useCreditInfo reports when GET /api/credits never
 * answered — the two are indistinguishable from the value alone, which is
 * how a free user whose credit fetch failed was told they had unlimited
 * access. Making the flag required means a call site that forgets it fails
 * to compile rather than resurrecting that bug silently.
 */
export function paywallStateFor(args: {
  authEnabled?: boolean;
  isSignedIn?: boolean;
  balance?: number | null;
  /** Whether GET /api/credits actually answered — useCreditInfo's `resolved`. */
  creditsResolved: boolean;
}): PaywallState {
  const { authEnabled, isSignedIn, balance, creditsResolved } = args;

  if (authEnabled && !isSignedIn) {
    return 'signed-out';
  }

  // Nothing is known about this account, so assert nothing about it. 'none'
  // renders nothing at all, which is the only honest option: every named
  // state below makes a claim (unlimited / has N / has none).
  if (!creditsResolved) {
    return 'none';
  }

  if (balance === null) {
    return 'unlimited';
  }

  if (balance !== undefined && balance >= 1) {
    return 'has-credits';
  }

  if (balance === 0) {
    return 'out-of-credits';
  }

  return 'none';
}

/**
 * Issue #953 — the credit model, stated once, at the only moment the funnel
 * had left silent.
 *
 * The arc before this point is honestly free: the teaser promises "no credit
 * card", and every free scan from the site is the full audit (#1782), locked.
 * Sign-up grants no credit (FREE_CREDITS = 0) and no audit. The first mention
 * of credits used to be the 402 that refused the second scan. This copy moves
 * that first mention to the moment the report lands, and frames it as what
 * the account holds. The pack size and price are env-configurable and are
 * never written here.
 */
const CREDIT_MODEL_SENTENCE = (
  <>
    One credit runs one full {FULL_AUDIT_CHECK_COUNT}-check audit of one URL.
    {' '}Every free scan runs the full audit.
    {' '}A credit unlocks its evidence and fixes.
  </>
);

const DELIVERED_REPORT_TITLE = 'This report is yours to keep';

/**
 * The balance sentence, or null when there is no resolved number to state.
 *
 * Assumption (documented per the safer-interpretation rule): only a finite,
 * non-negative number is a balance worth stating. `null` means unlimited and
 * `undefined` means unread — neither is a count, and inventing one here is
 * exactly the class of claim this epic exists to stop.
 */
function creditBalanceLine(balance?: number | null): string | null {
  if (typeof balance !== 'number' || !Number.isFinite(balance) || balance < 0) return null;
  return `You have ${balance} ${balance === 1 ? 'credit' : 'credits'} available.`;
}

export default function PaywallOffer({
  state,
  userId,
  target,
  balance,
  packPrice,
  creditsPerPurchase,
  onRunFullAudit,
  signInButton,
  context = 'report',
  reportId,
  onUnlocked,
  signedOutUnlock,
  consumer = false,
  clean = false,
}: PaywallOfferProps): React.ReactElement | null {
  const balanceLine = creditBalanceLine(balance);

  if (context === 'unlock' && state === 'signed-out' && signedOutUnlock) {
    return <SignedOutUnlockOffer packPrice={packPrice} consumer={consumer} clean={clean} reportId={reportId} {...signedOutUnlock} />;
  }

  if (context === 'unlock' && reportId
    && (state === 'unlimited' || state === 'has-credits' || state === 'out-of-credits')) {
    return (
      <UnlockOffer
        state={state}
        reportId={reportId}
        balance={balance}
        packPrice={packPrice}
        onUnlocked={onUnlocked}
        consumer={consumer}
        clean={clean}
      />
    );
  }

  // Issue #953. On the delivered-report surface every other state's copy is
  // wrong — 'unlimited' and 'has-credits' both offer to "Unlock Full Report",
  // and the report is already unlocked and on screen behind this card. Only
  // an account with a stated balance has a credit model to disclose:
  // 'unlimited' is not gated by credits at all, 'signed-out' has no account
  // to describe, and 'none' means the account was never read. All three stay
  // silent. 'out-of-credits' falls through to the shared branch below so the
  // buy control is built from the one `buttonLabel` the funnel contract
  // checks.
  if (context === 'report-delivered' && state !== 'out-of-credits') {
    if (state !== 'has-credits' || !balanceLine) return null;
    return (
      <div className="legacy-cta" data-testid="credit-model-note">
        <h2 className="legacy-cta__title">{DELIVERED_REPORT_TITLE}</h2>
        <p className="legacy-cta__desc" style={{ marginBottom: '12px' }}>
          {CREDIT_MODEL_SENTENCE}
        </p>
        <p className="legacy-cta__desc">{balanceLine}</p>
      </div>
    );
  }

  if (state === 'signed-out') {
    return (
      <div className="legacy-cta">
        <div className="legacy-cta__icon-wrapper">
          <LockShieldIcon />
        </div>
        {/* #1798: since the #1782 cutover an account holds no free audit and
            a claim starts no scan, so this card offers what sign-up does do
            (keep the report) and points the free audit at the marketing
            homepage, where it runs without an account. */}
        <h2 className="legacy-cta__title">
          Keep This Report
        </h2>
        <p className="legacy-cta__desc">
          Create a free account to keep this report on{' '}
          <span className="scan-hero__url">{target}</span>. The full {FULL_AUDIT_CHECK_COUNT}-check
          audit runs free, with no account, at{' '}
          <a href={freeAuditUrl()} style={planLinkStyle}>getprivisy.com</a>.
        </p>
        {signInButton || (
          <>
            <button className="legacy-cta__button">Create free account to claim</button>
            <button
              style={{
                display: 'block',
                margin: '12px auto 0',
                padding: '4px 16px',
                fontSize: '13px',
                fontWeight: 500,
                color: T.gray400,
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontFamily: T.fontBody,
              }}
            >
              Already have an account? Sign in
            </button>
          </>
        )}
      </div>
    );
  }

  if (state === 'unlimited') {
    return (
      <div className="legacy-cta">
        <div className="legacy-cta__icon-wrapper">
          <LockShieldIcon />
        </div>
        <h2 className="legacy-cta__title">
          Unlock Full Report
        </h2>
        <p className="legacy-cta__desc">
          Your account has unlimited access. Run a full audit to unlock all findings.
        </p>
        <button
          onClick={() => onRunFullAudit?.()}
          className="legacy-cta__button"
        >
          Run full audit
        </button>
      </div>
    );
  }

  if (state === 'has-credits') {
    return (
      <div className="legacy-cta">
        <h2 className="legacy-cta__title">
          Unlock Full Report
        </h2>
        <p className="legacy-cta__desc">
          Get a complete, detailed audit of <span className="scan-hero__url">{target}</span>.
        </p>
        <p className="legacy-cta__desc">
          Uses 1 credit from your account (you have {balance} remaining)
        </p>
        <button
          onClick={() => onRunFullAudit?.()}
          className="legacy-cta__button"
        >
          Run full audit - 1 credit
        </button>
      </div>
    );
  }

  if (state === 'out-of-credits') {
    const paymentLinkUrl = buildPaymentLinkUrl(userId);
    // Both halves of the offer, or neither: "Buy undefined full audits for $49"
    // is worse than no control at all, and half a quote is still a quote the
    // buyer would be held to (#970).
    const canQuoteOffer = packPrice !== undefined && creditsPerPurchase !== undefined;

    // No signed-in user means no way to attribute a purchase, and unknown
    // pack pricing means nothing truthful to put on the control, so in either
    // case there is no buy control to render. On the report surface the
    // locked report is still on screen around this component, so rendering
    // nothing stays correct (unchanged from Task 2). On the scan surface this
    // component *is* the entire response to a 402 — returning null there
    // leaves an empty page and the user with no idea why the scan they just
    // submitted never ran — so the explanation renders and only the buy
    // control is omitted.
    if ((!paymentLinkUrl || !canQuoteOffer) && context === 'report') return null;

    // 'report' branch renders byte-identically to before the `context` prop
    // existed (Task 2's reviewed copy, LockedReportView's only caller).
    // 'scan' is issue #943 fix round 1: no report exists yet on this path,
    // so the copy says plainly that the scan didn't run and why, instead of
    // reusing report-shaped language that doesn't apply. 'scan-entry' (#971)
    // is the same offer before anything has been attempted — nothing failed,
    // so it states the account's position and what a credit buys.
    const title = context === 'report'
      ? 'Unlock Full Report'
      : context === 'scan'
        ? 'Audit Not Started'
        : context === 'report-delivered'
          ? DELIVERED_REPORT_TITLE
          : 'Add Credits to Run an Audit';
    const description = context === 'report'
      ? <>You&apos;ll come right back to unlock this report</>
      : context === 'scan'
        ? <>You&apos;re out of credits, so this audit didn&apos;t run.</>
        : context === 'report-delivered'
          ? CREDIT_MODEL_SENTENCE
          : <>You&apos;re out of credits. Each credit runs one full audit of one URL.</>;
    // Built only when both halves of the offer are known; `canQuoteOffer`
    // narrows packPrice/creditsPerPurchase to numbers here. Every surface
    // shares one label so the balance-driven offer, the 402 offer and the
    // report quote the pack identically (#971).
    const buttonLabel = !canQuoteOffer
      ? null
      : `Buy ${formatPackContents(creditsPerPurchase)} for ${formatPriceFromCents(packPrice)}`;

    return (
      // The scan surface replaced an ApiErrorNotice, which carried
      // role="alert". Without a live region a screen-reader user gets no
      // signal that the scan didn't run: `error` stays null on the credit
      // path, so nothing else on the page announces either. "status" rather
      // than "alert" — this is an offer, so it is announced politely instead
      // of interrupting. The report and scan-entry surfaces are page content
      // the user navigated to, not announcements, and stay unannotated (which
      // also keeps the report markup byte-identical to Task 2).
      <div
        className="legacy-cta"
        role={context === 'scan' ? 'status' : undefined}
        data-testid={context === 'report-delivered' ? 'credit-model-note' : undefined}
      >
        {/* No lock on the delivered-report surface: nothing here is locked,
            and lock iconography would read as a retraction of the report the
            user is holding. */}
        {context !== 'report-delivered' && (
          <div className="legacy-cta__icon-wrapper">
            <LockShieldIcon />
          </div>
        )}
        <h2 className="legacy-cta__title">
          {title}
        </h2>
        <p
          className="legacy-cta__desc"
          style={context === 'report-delivered' ? { marginBottom: '12px' } : undefined}
        >
          {description}
        </p>
        {context === 'report-delivered' && balanceLine && (
          <p className="legacy-cta__desc">{balanceLine}</p>
        )}
        {paymentLinkUrl && canQuoteOffer && (
          <a
            href={paymentLinkUrl}
            onClick={stashPostPurchaseRoute}
            className="legacy-cta__button"
            style={{ display: 'inline-block', textAlign: 'center' }}
          >
            {buttonLabel}
          </a>
        )}
        {paymentLinkUrl && canQuoteOffer && context !== 'report-delivered' && <PlanOptions />}
      </div>
    );
  }

  // state === 'none': balance unresolved or otherwise unrecognized — render
  // nothing, matching LockedReportView's original bare `return null` for
  // anything that didn't match a named branch.
  return null;
}

/**
 * #1762: the offer on a locked full audit. The audit already ran, so every
 * path here unlocks that report in place and none reruns it: a credit when
 * the account has one (or no charge for an unlimited account), the unlock
 * checkout when it has none, and monitoring as the alternative in either case.
 *
 * #1776: both checkouts carry the report id, and the Stripe webhook unlocks
 * it server-side (#1775). The unlock checkout returns to this report with a
 * session id the scan route waits on; nothing is stashed for the browser to
 * spend after the purchase.
 */
function UnlockOffer({
  state,
  reportId,
  balance,
  packPrice,
  onUnlocked,
  consumer,
  clean,
}: {
  state: 'unlimited' | 'has-credits' | 'out-of-credits';
  reportId: string;
  balance?: number | null;
  packPrice?: number;
  onUnlocked?: () => void;
  consumer: boolean;
  clean: boolean;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A 402 means the balance went stale between render and click: show the
  // buy offer instead of a button that cannot succeed.
  const [sawInsufficient, setSawInsufficient] = useState(false);
  const outOfCredits = state === 'out-of-credits' || sawInsufficient;
  // The state flag lags a render behind a fast double click; the ref does not.
  const inFlight = useRef(false);

  const handleUnlock = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    recordPaywallClick(reportId, 'unlock_credit');
    try {
      await unlockReport(reportId);
      onUnlocked?.();
    } catch (err) {
      if (isInsufficientCredits(err)) {
        setSawInsufficient(true);
      } else {
        setError('We could not unlock this report. Nothing was charged; please try again.');
      }
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  const handleMonitor = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    recordPaywallClick(reportId, 'monitor');
    try {
      trackInitiateCheckout({ reportId, plan: 'monitor' });
      const { url } = await createBillingCheckoutSession('monitor', 'annual', reportId, readMetaAttribution());
      billingRedirect.assign(url);
    } catch {
      inFlight.current = false;
      setPending(false);
      setError('We could not start the monitoring checkout. Please try again.');
    }
  };

  const handleBuyUnlock = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    recordPaywallClick(reportId, 'unlock_purchase');
    try {
      trackInitiateCheckout({ reportId, plan: 'unlock', value: 49 });
      const { url } = await createUnlockCheckout(reportId, readMetaAttribution());
      billingRedirect.assign(url);
    } catch (err) {
      inFlight.current = false;
      setPending(false);
      // Unlocked since this page rendered (another tab, monitoring): show it.
      if (err instanceof ApiError && err.apiCode === 'REPORT_ALREADY_UNLOCKED') {
        onUnlocked?.();
        return;
      }
      setError('We could not start the checkout. Nothing was charged; please try again.');
    }
  };

  const unlockLabel = state === 'unlimited'
    ? 'Unlock this report'
    : `Unlock this report · 1 credit (you have ${balance})`;

  return (
    <div className="legacy-cta" data-testid="unlock-offer">
      <div className="legacy-cta__icon-wrapper">
        <LockShieldIcon />
      </div>
      <h2 className="legacy-cta__title">Unlock Full Report</h2>
      <UnlockDescription consumer={consumer} clean={clean} />
      {!outOfCredits && (
        <button
          onClick={handleUnlock}
          disabled={pending}
          className="legacy-cta__button"
        >
          {unlockLabel}
        </button>
      )}
      {outOfCredits && packPrice !== undefined && (
        <button
          onClick={handleBuyUnlock}
          disabled={pending}
          className="legacy-cta__button"
        >
          {`Unlock for ${formatPriceFromCents(packPrice)}`}
        </button>
      )}
      {error && (
        <p className="legacy-cta__desc" role="alert" style={{ marginTop: '12px', color: T.error }}>
          {error}
        </p>
      )}
      <p
        className="legacy-cta__desc"
        data-testid="unlock-monitor-offer"
        style={{ margin: '20px auto 0', fontSize: '14px' }}
      >
        Or{' '}
        <button
          type="button"
          onClick={handleMonitor}
          disabled={pending}
          style={{ font: 'inherit', background: 'none', border: 'none', padding: 0, cursor: 'pointer', ...planLinkStyle }}
        >
          monitor this site weekly
        </button>
        {' '}for {formatPriceFromCents(MONITOR_ANNUAL_PRICE_CENTS)}/year. Unlocks this report too.
      </p>
    </div>
  );
}

/**
 * The unlock offers' description: owner copy, #2046's consumer copy, or, on
 * a site with nothing failed, the record the report holds.
 */
function UnlockDescription({ consumer, clean }: { consumer: boolean; clean: boolean }) {
  return (
    <p className="legacy-cta__desc">
      {consumer ? CONSUMER_UNLOCK_DESC : clean ? (
        <>
          All {FULL_AUDIT_CHECK_COUNT} checks already ran on this site. Unlock the report to see
          every check it passed and every third-party vendor that loaded.
        </>
      ) : (
        <>
          All {FULL_AUDIT_CHECK_COUNT} checks already ran on this site. Unlock the report to see
          every finding with its evidence and how to fix it.
        </>
      )}
    </p>
  );
}

/**
 * #1779: a signed-out visitor's locked full audit. Holding the report's claim
 * token (2026-10-27), the visitor pays first: the button opens the unlock
 * checkout, and the webhook gives the purchase to the account that holds the
 * checkout email, creating it when needed. Without the token the click
 * records the intent and opens sign-up; the claim after sign-up starts the
 * checkout. The price is the one the signed-in unlock checkout charges,
 * omitted while it is unknown (#970), and nothing here mentions credits:
 * this visitor has none.
 */
function SignedOutUnlockOffer({
  packPrice,
  returnHash,
  onUnlockSignedOut,
  onBeforeCheckout,
  intentToken,
  onKeepSignedOut,
  reportId,
  consumer,
  clean,
}: SignedOutUnlock & { packPrice?: number; reportId?: string | null; consumer: boolean; clean: boolean }) {
  const signUpUrl = welcomeRedirectUrl(returnHash);
  const signInUrl = `${window.location.pathname}${window.location.search}${returnHash}`;
  const label = packPrice !== undefined
    ? `Unlock this report for ${formatPriceFromCents(packPrice)}`
    : 'Unlock this report';
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const payFirst = Boolean(intentToken && reportId);

  const handlePayFirst = async () => {
    if (!reportId || !intentToken || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(null);
    onBeforeCheckout?.();
    recordPaywallClick(reportId, 'unlock_purchase');
    try {
      trackInitiateCheckout({ reportId, plan: 'unlock', value: 49 });
      const { url } = await createUnlockCheckout(reportId, readMetaAttribution(), intentToken);
      billingRedirect.assign(url);
    } catch (err) {
      inFlight.current = false;
      setPending(false);
      setError(err instanceof ApiError && err.status === 401
        ? 'This report belongs to an account now. Sign in to unlock it.'
        : 'We could not start the checkout. Nothing was charged; please try again.');
    }
  };

  const handleSignUpFirst = () => {
    if (reportId) recordPaywallClick(reportId, 'unlock_purchase');
    onUnlockSignedOut();
  };

  return (
    <div className="legacy-cta" data-testid="signed-out-unlock-offer">
      <div className="legacy-cta__icon-wrapper">
        <LockShieldIcon />
      </div>
      <h2 className="legacy-cta__title">Unlock Full Report</h2>
      <UnlockDescription consumer={consumer} clean={clean} />
      {/* This visitor has never seen an unlocked report; show them one
          before asking them to pay for it. A new tab keeps this report. */}
      <p className="legacy-cta__desc" style={{ margin: '0 0 16px', fontSize: '14px' }}>
        <a href={sampleReportUrl()} target="_blank" rel="noopener noreferrer" style={planLinkStyle}>
          See what an unlocked report looks like
        </a>
      </p>
      {payFirst ? (
        <>
          <button type="button" className="legacy-cta__button" onClick={handlePayFirst} disabled={pending}>
            {label}
          </button>
          <p className="legacy-cta__desc" style={{ marginTop: '12px', fontSize: '13px' }}>
            No account needed first. We set one up for the email you pay with.
          </p>
          {/* #2130: pay-first took the sign-up off the card; keeping the report free is how it gets claimed. */}
          <SignUpButton
            mode="modal"
            forceRedirectUrl={signUpUrl}
            signInForceRedirectUrl={signInUrl}
            appearance={clerkAppearance}
          >
            <button type="button" onClick={onKeepSignedOut} style={secondaryLinkStyle}>
              Or create a free account to keep this report
            </button>
          </SignUpButton>
        </>
      ) : (
        <SignUpButton
          mode="modal"
          forceRedirectUrl={signUpUrl}
          signInForceRedirectUrl={signInUrl}
          appearance={clerkAppearance}
        >
          <button type="button" className="legacy-cta__button" onClick={handleSignUpFirst}>
            {label}
          </button>
        </SignUpButton>
      )}
      {error && (
        <p className="legacy-cta__desc" role="alert" style={{ marginTop: '12px', color: T.error }}>
          {error}
        </p>
      )}
      <SignInButton
        mode="modal"
        forceRedirectUrl={signInUrl}
        signUpForceRedirectUrl={signUpUrl}
        appearance={clerkAppearance}
      >
        <button type="button" onClick={onUnlockSignedOut} style={secondaryLinkStyle}>
          Already have an account? Sign in
        </button>
      </SignInButton>
    </div>
  );
}

const secondaryLinkStyle: React.CSSProperties = {
  display: 'block',
  margin: '12px auto 0',
  padding: '4px 16px',
  fontSize: '13px',
  fontWeight: 500,
  color: T.gray400,
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: T.fontBody,
};

const planLinkStyle: React.CSSProperties = {
  color: T.cobalt400,
  fontWeight: 600,
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
};

/**
 * Issue #1320: the other two plans from the pricing page, offered next to the
 * single-audit buy control. Neither quotes a price. The monitoring Price lives
 * in Stripe (STRIPE_MONITOR_ANNUAL_PRICE_ID) and no endpoint reports it, so a
 * figure here would be a literal that can go stale, the defect #970 removed
 * from the pack offer. The billing page owns the monitoring Checkout (#1315),
 * and agency is contact-us until #1324.
 *
 * Assumption (safer reading): this renders only alongside the buy control, so
 * a surface that cannot state the single-audit offer states none of them.
 * An existing monitor subscriber who runs out of credits still sees the
 * monitoring link; the billing page then shows "Manage subscription" rather
 * than a second Checkout, and teaching paywallStateFor about subscribers is
 * #1316's to do, not this component's.
 */
function PlanOptions() {
  const marketingUrl = process.env.REACT_APP_MARKETING_URL || 'https://getprivisy.com';
  return (
    <p
      className="legacy-cta__desc"
      data-testid="paywall-plan-options"
      style={{ margin: '20px auto 0', fontSize: '14px' }}
    >
      Or <a href="#/billing" style={planLinkStyle}>monitor a site weekly</a> on an annual plan,
      {' '}or ask about <a href={`${marketingUrl.replace(/\/$/, '')}/contact`} style={planLinkStyle}>agency pricing</a>.
    </p>
  );
}

function LockShieldIcon() {
  return (
    <svg
      width="24" height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke={T.cobalt400}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <rect x="9" y="11" width="6" height="5" rx="1" />
      <path d="M12 11V9a2 2 0 0 1 4 0" />
    </svg>
  );
}
